import React, { useEffect, useState } from "react";
import { Layout, Menu, Skeleton, Tooltip } from "antd";
import { DatabaseOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "config/token";
import axios from "axios";
import { NEXUS_API_BASE_URL } from "const";
import { IDomainsConfig } from "pages/Ponds/types";
import type { MenuProps } from "antd";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

// Custom component for inactive menu items with tooltip
const InactiveMenuItem: React.FC<{ label: React.ReactNode }> = ({ label }) => (
  <Tooltip title={<><strong>{label}</strong> Domain is not yet available</>} placement="right">
    <span style={{ color: "#8c8c8c", cursor: "not-allowed", display: "block", width: "100%" }}>
      {label}
    </span>
  </Tooltip>
);

// Custom component for active menu items with tooltip
const ActiveMenuItem: React.FC<{ label: React.ReactNode }> = ({ label }) => (
  <Tooltip title={<><strong>{label}</strong></>} placement="right">
    <span style={{ display: "block", width: "100%" }}>
      {label}
    </span>
  </Tooltip>
);

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  title?: string,
  active?: string,
): MenuItem {
  const isInactive = active === "0";
  
  return {
    key,
    icon,
    children,
    label: isInactive ? <InactiveMenuItem label={label} /> : <ActiveMenuItem label={label} />,
    title,
    active,
    className: isInactive ? "inactive-menu-item" : "",
  } as MenuItem;
}

export interface SidebarProps {
  collapsed: boolean;
  collapsedWidth?: number;
  width?: number;
  selectedDomainKey?: string;
  onCollapse: (collapsed: boolean) => void;
  onDomainSelect: (domain: IDomainsConfig) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  collapsedWidth = 80,
  width = 300,
  selectedDomainKey,
  onCollapse,
  onDomainSelect,
}) => {
  const msalInstance = useMsal();
  const [loading, setLoading] = useState(true);
  const [domainConfigs, setDomainConfigs] = useState<IDomainsConfig[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  // Sorting logic: Active domains first, then inactive
  function sortDomains(domains: IDomainsConfig[]) {
    return [...domains].sort((a, b) => {
      const aActive = a.active === "1" ? 0 : 1;
      const bActive = b.active === "1" ? 0 : 1;
      return aActive - bActive;
    });
  }

  useEffect(() => {
    const fetchDomains = async () => {
      setLoading(true);
      let domains: IDomainsConfig[] = [];
      const cached = localStorage.getItem("domainsData");
      if (cached) {
        try {
          domains = JSON.parse(cached);
        } catch {
          domains = [];
        }
      }
      if (!domains || !Array.isArray(domains) || domains.length === 0) {
        // Fetch from API
        try {
          const token = await getAccessToken(msalInstance);
          const bearerToken = "Bearer " + (token?.token ?? "");
          const res = await axios.get(
            `${NEXUS_API_BASE_URL}/nexus/get-domains`,
            { headers: { Authorization: bearerToken } },
          );
          domains = res.data;
          localStorage.setItem("domainsData", JSON.stringify(domains));
        } catch (error) {
          console.error("Error fetching domains:", error);
          domains = [];
        }
      }
      setDomainConfigs(domains);

      // Sort domains (active on top)
      const sortedDomains = sortDomains(domains);

      setMenuItems([
        getItem(
          "Data Domains",
          "data_domains",
          <DatabaseOutlined />,
          sortedDomains.map((item: IDomainsConfig) =>
            getItem(
              item.display_name,
              item.display_name,
              undefined,
              undefined,
              undefined,
              item.active,
            ),
          ),
        ),
      ]);
      setLoading(false);
    };
    fetchDomains();
  }, [msalInstance]);

  // Recursive search for the clicked item
  const findItem = (items: MenuProps["items"], key: string): any => {
    if (!items) return null;
    for (const item of items) {
      if (!item) continue;
      if (item.key === key) return item;
      if ("children" in item && item.children) {
        const found = findItem(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const handleMenuClick: MenuProps["onClick"] = (info) => {
    const clickedItem = findItem(menuItems, info.key);
    const domain = domainConfigs.find((d) => d.display_name === info.key);
    
    // Skip processing for inactive items (they should be disabled anyway)
    if (clickedItem && clickedItem.active === "0") {
      return;
    }
    
    if (domain) {
      onDomainSelect(domain);
    }
  };

  // Figure out what to select by default
  const defaultSelectedKeys = selectedDomainKey ? [selectedDomainKey] : [];

  // Helper: check if menu has loaded domains
  const isMenuLoaded = () => {
    if (!Array.isArray(menuItems) || menuItems.length === 0) return false;
    const firstItem = menuItems[0];
    if (!firstItem || firstItem.key !== "data_domains") return false;
    // Check if submenu with children
    const hasChildren =
      "children" in firstItem && Array.isArray(firstItem.children) && firstItem.children.length > 0;
    return hasChildren;
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      collapsedWidth={collapsedWidth}
      width={width}
      breakpoint="md"
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1,
      }}
    >
      {!collapsed && (loading || !isMenuLoaded()) ? (
        <div style={{ marginTop: "100px", padding: "0 16px" }}>
          <Skeleton active paragraph={{ rows: 10 }} title={false} />
          <Skeleton active paragraph={{ rows: 8 }} title={false} style={{ marginTop: 20 }} />
        </div>
      ) : (
        <Menu
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={defaultSelectedKeys}
          defaultSelectedKeys={defaultSelectedKeys}
          defaultOpenKeys={["data_domains"]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            marginTop: "100px",
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            paddingBottom: "50px",
          }}
        />
      )}
    </Sider>
  );
};

export default Sidebar;
